import {
    afterNextRender,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    inject,
    Injector,
    OnInit,
    signal,
    ViewChild
} from '@angular/core';
import {DatePipe, formatDate} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatButtonModule} from '@angular/material/button';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {RegistrationService} from '../services/registration.service';
import {ProjectService} from '../services/project.service';
import {Registration} from '../classes/registration';
import {Project} from '../classes/project';
import {CodaltComponent} from '../codalt.component';
import {debounceTime, distinctUntilChanged, interval, of, switchMap} from 'rxjs';
import {Utils} from '../utils.class';
import {AuthorisationService} from '../services/auth/authorisation.service';
import {CustomerColorPipe} from '../shared/customer-color.pipe';
import {InfoBannerComponent} from '../shared/ui/info-banner/info-banner.component';
import {KbdComponent} from '../shared/ui/kbd/kbd.component';
import {PageComponent} from '../shared/ui/page/page.component';
import {PeriodStepperComponent} from '../shared/ui/period-stepper/period-stepper.component';
import {SectionComponent} from '../shared/ui/section/section.component';

/** One row of the grid: a leaf work project with its customer and parent path (presentation only). */
interface RegistrationRow {
    project: Project;
    task: string;
    /** "Codalt › CRM26" below the task */
    sub: string;
    client: string;
    clientFull: string;
    companyId: number | null;
    title: string;
}

interface SearchGroup {
    client: string;
    companyId: number | null;
    items: { row: RegistrationRow; index: number; added: boolean }[];
}

interface QuarterCell {
    time: string;
    /** "HH:mm", the key of the comments object */
    key: string;
    label: string;
    off: boolean;
    hourStart: boolean;
}

interface HourCell {
    time: number;
    hour: number;
    label: string;
    off: boolean;
}

interface Block {
    from: number;
    left: number;
    width: number;
}

type ReloadState = 'idle' | 'busy' | 'done';

/** The grid covers 24 hours (12 visible in the width); other days than today open scrolled to 07:00. */
const OPEN_AT_HOUR = 7;
/** Office hours: outside 08–18 the grid is light grey. */
const OFFICE_START_HOUR = 8;
const OFFICE_END_HOUR = 18;
const DAY_MS = 24 * 60 * 60 * 1000;

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss'],
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatDatepickerModule,
        MatInputModule,
        MatFormFieldModule,
        CustomerColorPipe,
        InfoBannerComponent,
        KbdComponent,
        PageComponent,
        PeriodStepperComponent,
        SectionComponent,
    ],
})
export class RegistrationComponent extends CodaltComponent implements OnInit {
    public currentTime = new Date();
    public startTime = this.getStartOfDay(new Date());

    /** The horizontally scrolling grid (only this element scrolls to the current time, never the page). */
    @ViewChild('registrationhours') registrationhours: ElementRef<HTMLElement>;
    @ViewChild('comment') commentElement: ElementRef;

    searchNewProjectText = new FormControl<string>('');

    readonly projects = signal<Project[]>([]);
    readonly registrations = signal<Registration[]>([]);
    readonly searchNewProjects = signal<Project[]>([]);
    readonly quarters = signal<{ time: string }[]>([]);
    readonly hours = signal<Date[]>([]);
    readonly comments = signal<Record<string, string>>({});
    readonly saveButtonText = signal<string>('Opslaan');
    readonly showErrorMessage = signal<boolean>(false);

    selectedDate: Date = new Date();
    commentProject: Project | null = null;
    commentQuarter: { time: string } | null = null;
    tempComment = new FormControl<string>('');
    readonly reloadState = signal<ReloadState>('idle');
    lastMovedQuarter: Date | null = null;
    currentChangingProjectId: number | null = null;
    currentRegistering: boolean | null = null;
    isWorking = false;
    hoveredProject: number | null = null;
    showCommentModal = false;

    // Presentation state
    readonly now = signal(new Date());
    readonly searchQuery = toSignal(this.searchNewProjectText.valueChanges, {initialValue: ''});
    /** The search text the current results belong to (to tell "no results" from "still searching"). */
    readonly searchedFor = signal<string>('');
    readonly searchFocused = signal(false);
    readonly searchIndex = signal(0);
    commentPosition = {x: 0, y: 0};
    commentRow: RegistrationRow | null = null;

    readonly rows = computed(() => this.flatten(this.projects()));
    readonly searchResults = computed(() => this.flatten(this.searchNewProjects()));
    readonly searchGroups = computed<SearchGroup[]>(() => {
        const visible = new Set(this.rows().map(row => row.project.id));
        const groups: SearchGroup[] = [];
        this.searchResults().forEach((row, index) => {
            let group = groups.find(g => g.client === row.clientFull);
            if (!group) {
                group = {client: row.clientFull, companyId: row.companyId, items: []};
                groups.push(group);
            }
            group.items.push({row, index, added: visible.has(row.project.id)});
        });
        return groups;
    });

    readonly hourCells = computed<HourCell[]>(() => {
        return this.hours().map(date => ({
            time: date.getTime(),
            hour: date.getHours(),
            label: this.pad(date.getHours()) + ':00',
            off: date.getHours() < OFFICE_START_HOUR || date.getHours() >= OFFICE_END_HOUR,
        }));
    });

    readonly quarterCells = computed<QuarterCell[]>(() => {
        return this.quarters().map(quarter => {
            const date = new Date(quarter.time);
            return {
                time: quarter.time,
                key: this.commentKey(quarter.time),
                label: this.quarterLabel(date),
                off: date.getHours() < OFFICE_START_HOUR || date.getHours() >= OFFICE_END_HOUR,
                hourStart: date.getMinutes() === 0,
            };
        });
    });

    private readonly injector = inject(Injector);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private scrollKey: string | null = null;
    /** Registrations + comments as loaded, to show "Opgeslagen" vs "Opslaan" (presentation only). */
    private savedSnapshot = '';

    constructor(
        private registrationService: RegistrationService,
        private projectService: ProjectService,
        private ref: ChangeDetectorRef
    ) {
        super();

        // Scroll the grid (not the page) to the current time on today, or to 07:00 on other days,
        // whenever another day is loaded (loadRegistrations sets a new hours array).
        effect(() => {
            this.hours();
            const key = this.dayKey(this.selectedDate);
            if (key !== this.scrollKey) {
                this.scrollKey = key;
                afterNextRender(() => this.scrollToFocus(), {injector: this.injector});
            }
        });

        // Snapshot of the loaded data; edits mutate the arrays in place, so this only runs on (re)load.
        effect(() => {
            this.registrations();
            this.comments();
            this.savedSnapshot = this.snapshot();
        });
    }

    public getDifferenceInMinutes(date1: Date, date2: Date): number {
        return Math.floor((date1.getTime() - date2.getTime()) / 60000);
    }

    ngOnInit() {
        // Keep the red "now" line up to date
        this.subscriptions.add(
            interval(60000).subscribe(() => {
                this.currentTime = new Date();
                this.now.set(this.currentTime);
            }),
        );

        this.subscriptions.add(
            this.searchNewProjectText.valueChanges
                .pipe(
                    debounceTime(150),
                    distinctUntilChanged(),
                    switchMap((value) => {
                        this.searchNewProjects.set([]);

                        if (value.length > 2) {
                            return this.projectService.findHiddenProject(value, this.selectedDate);
                        } else {
                            return of([]);
                        }
                    }),
                )
                .subscribe((value) => {
                    if (value.length > 0) {
                        this.searchNewProjects.set(value);
                    } else {
                        this.searchNewProjects.set([]);
                    }
                    this.searchedFor.set(this.searchNewProjectText.value ?? '');
                    this.searchIndex.set(0);
                }),
        );

        // ?date=yyyy-MM-dd (e.g. from a day on the Daily) opens that day; the parameter is removed again.
        // An impossible date (2026-02-31) is ignored instead of rolling over to another day.
        const dateParam = this.route.snapshot.queryParamMap.get('date');
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateParam ?? '')) {
            const [year, month, day] = dateParam.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
                this.router.navigate([], {relativeTo: this.route, queryParams: {date: null}, queryParamsHandling: 'merge', replaceUrl: true});
                this.selectedDateChanged(date);
                return;
            }
        }

        let selectedDate = localStorage.getItem('selected_date');
        if (selectedDate) {
            this.selectedDateChanged(new Date(selectedDate));
        } else {
            this.loadRegistrations();
        }
    }

    public projectHover(projectId: number) {
        this.hoveredProject = projectId;
    }

    public projectLeave() {
        this.hoveredProject = null;
    }

    public loadRegistrations() {
        this.quarters.set([]);
        const hoursArray: Date[] = [];
        const startOfDay = this.getStartOfDay(this.selectedDate);

        for (let i: number = 0; i < 24 * 4; i++) {
            this.quarters.update((quarters) => [
                ...quarters,
                {time: this.addMinutes(startOfDay, i * 15).toISOString()},
            ]);
            if (i % 4 == 0) {
                hoursArray.push(this.addMinutes(startOfDay, i * 15));
            }
        }
        this.hours.set(hoursArray);

        this.projects.set([]);
        this.registrations.set([]);
        this.subscriptions.add(
            this.projectService.getVisible(this.selectedDate).subscribe((data) => {
                this.projects.set(data);
                if (this.reloadState() === 'busy') {
                    this.reloadState.set('done');
                    setTimeout(() => this.reloadState.set('idle'), 1500);
                }
            }),
        );
        this.subscriptions.add(
            this.registrationService.getAll(this.selectedDate).subscribe((response) => {
                this.registrations.set(response.registrations || []);
                this.comments.set(response.comments || {});
                if (Array.isArray(this.comments())) {
                    this.comments.set({});
                }
            }),
        );
    }

    reload() {
        this.reloadState.set('busy');
        this.projectService.azure().then(
            () => {
                this.loadRegistrations();
            },
            () => {
                this.loadRegistrations();
            },
        );
    }

    selectedDateChanged(event: Date) {
        this.selectedDate = event;
        this.selectedDate.setHours(10);

        if (this.isSameDay(this.selectedDate, new Date())) {
            localStorage.removeItem('selected_date');
        } else {
            const datePipe = new DatePipe('en-US');
            localStorage.setItem('selected_date', datePipe.transform(event, 'yyyy-MM-dd') || '');
        }

        const canEditByDate = Utils.canEditRegistrationDate(this.selectedDate, this.currentTime, AuthorisationService.hasRole(this.Roles.MutationPreviousPeriod), AuthorisationService.hasRole(this.Roles.MutationPrevPreviousPeriod));
        if (!canEditByDate) {
            this.showErrorMessage.set(true);
        } else {
            this.showErrorMessage.set(false);
        }

        this.loadRegistrations();
    }

    public save() {
        this.saveButtonText.set('Opslaan…');
        this.subscriptions.add(
            this.registrationService
                .save(this.selectedDate, this.registrations(), this.comments())
                .subscribe(() => {
                    this.saveButtonText.set('Opgeslagen');
                    setTimeout(() => this.saveButtonText.set('Opslaan'), 1500);
                    this.loadRegistrations()
                }),
        );
    }

    public continue() {
        let latestOther: Registration | null = null;
        const now = new Date();

        this.registrations().forEach((registration) => {
            if (this.isBefore(new Date(registration.till), now)) {
                if (latestOther) {
                    // But when latestOther is set, the till should also be later than current latestOther
                    if (this.isBefore(new Date(latestOther.till), new Date(registration.till))) {
                        latestOther = registration;
                    }
                } else {
                    latestOther = registration;
                }
            }
        });

        if (latestOther) {
            const currentMinutes = now.getMinutes();
            let remainder = (currentMinutes % 15) * -1;
            if (remainder >= -7) {
                remainder = remainder - 15;
            }

            const nearest = new Date(now);
            nearest.setMinutes(currentMinutes + remainder);
            nearest.setSeconds(0);
            nearest.setMilliseconds(0);

            let event = new MouseEvent('mousemove', {shiftKey: true});

            this.register(nearest, latestOther.work_project_id, event);
        }
    }

    public addComment(quarter: any, project: any) {
        this.commentProject = project;
        this.commentQuarter = quarter;
        this.showCommentModal = true;

        let indexTime = new Date(this.commentQuarter.time).toString().substring(16, 21);
        this.tempComment.setValue(this.comments()[indexTime] || '');

        setTimeout(() => this.commentElement?.nativeElement.focus());
        return false;
    }

    public totalTime(projectId: number = null) {
        let time = 0;
        let projectRegistrations = projectId
            ? this.registrations().filter((p) => p.work_project_id == projectId)
            : this.registrations();

        projectRegistrations.forEach((reg) => {
            time += new Date(reg.till).getTime() - new Date(reg.from).getTime();
        });
        if (time > 0) {
            let minutes = time / 1000 / 60;
            return Math.floor(minutes / 60) + ':' + ('0' + (minutes % 60)).slice(-2);
        }
        return '';
    }

    public lg(var2) {
        let quarter = new Date(var2.target.dataset['quarter']);
        let workproject = var2.target.dataset['workproject'];
        if (var2.target.className.indexOf('quarter') == -1) {
            return false;
        }
        if (var2.type == 'mousedown') {
            this.currentChangingProjectId = +workproject;
            this.currentRegistering = !this.quarterSelected(this.currentChangingProjectId, quarter);
        }
        if (var2.type == 'mouseup') {
            this.lastMovedQuarter = null;
            this.currentChangingProjectId = null;
            this.currentRegistering = null;
        } else if (var2.buttons == 1 || var2.type == 'click') {
            if (this.lastMovedQuarter != quarter || var2.type == 'click') {
                this.lastMovedQuarter = quarter;
                this.register(quarter, this.currentChangingProjectId || workproject, var2);
            }
        }
    }

    public setComment() {
        if (!this.commentQuarter) return;

        let indexTime = new Date(this.commentQuarter.time).toString().substring(16, 21);
        if (this.tempComment.value === '') {
            delete this.comments()[indexTime];
        } else {
            this.comments()[indexTime] = this.tempComment.value;
        }
        this.showCommentModal = false;
    }

    clearComment() {
        this.tempComment.setValue('');
        this.setComment()
    }

    public register(quarter: any, project: any, event: MouseEvent) {
        if (this.isWorking) {
            return;
        }

        quarter = new Date(quarter);
        project = +project;

        const canEditByDate = Utils.canEditRegistrationDate(quarter, this.currentTime, AuthorisationService.hasRole(this.Roles.MutationPreviousPeriod), AuthorisationService.hasRole(this.Roles.MutationPrevPreviousPeriod));

        if (!canEditByDate) {
            this.showErrorMessage.set(true);
            return false
        }

        this.showErrorMessage.set(false);
        this.isWorking = true;

        let newRegistration = new Registration();
        newRegistration.work_project_id = project;
        newRegistration.from = quarter;
        newRegistration.till = this.addMinutes(quarter, 15);
        let impossible = false;

        // Loop through registrations and check whether there is already work registered this time
        this.registrations().forEach((registration) => {
            // Is this registration same or around (starting before, ending after) new quarter?
            if (
                this.isSameOrBefore(new Date(registration.from), newRegistration.from) &&
                this.isSameOrAfter(new Date(registration.till), newRegistration.till)
            ) {
                // If same project, unregister
                if (
                    this.currentRegistering === false &&
                    newRegistration.work_project_id == registration.work_project_id
                ) {
                    if (this.isSame(new Date(registration.from), newRegistration.from)) {
                        if (this.isSame(new Date(registration.till), newRegistration.till)) {
                            // from and till both same, simple splice
                            this.registrations().splice(
                                this.registrations().indexOf(registration),
                                1,
                            );
                        } else {
                            // From equal, till different
                            // Start the registration from the end of the clicked quarter
                            registration.from = newRegistration.till;
                        }
                    }
                    // From not equal, but till is equal?
                    else if (this.isSame(new Date(registration.till), newRegistration.till)) {
                        // Then end registration on the from moment of clicked quarter
                        registration.till = newRegistration.from;
                    } else {
                        // There is a registration around, but start or end is not matching clicked quarter

                        // If ctrlKey down, we have to remove the complete registration
                        if (event.ctrlKey) {
                            this.registrations().splice(
                                this.registrations().indexOf(registration),
                                1,
                            );
                        } else {
                            // Else we have to splice the registration in 2 registrations
                            let extraReg = new Registration();
                            extraReg.work_project_id = newRegistration.work_project_id;
                            extraReg.from = newRegistration.till;
                            extraReg.till = registration.till;
                            this.registrations().push(extraReg);

                            registration.till = newRegistration.from;
                        }
                    }
                }

                // Never register new times
                impossible = true;
            }
        });

        if (this.currentRegistering === false) {
            impossible = true;
        }

        // When a quarter was clicked which have already a registration
        // We never register new times, so return
        if (impossible) {
            this.isWorking = false;
            return false;
        }

        let extendedOther = false;

        // When shiftKey is pressed, we should extend the latest project
        if (event.shiftKey) {
            let latestOther = null;
            this.registrations().forEach((registration) => {
                // When till is before the start of new registration, we have a potential latestOther
                if (this.isSameOrBefore(new Date(registration.till), newRegistration.from)) {
                    if (latestOther) {
                        // But when latestOther is set, the till should also be later than current latestOther
                        if (
                            this.isSameOrBefore(
                                new Date(latestOther.till),
                                new Date(registration.till),
                            )
                        ) {
                            latestOther = registration;
                        }
                    } else {
                        latestOther = registration;
                    }
                }
            });
            if (latestOther) {
                if (latestOther.work_project_id == newRegistration.work_project_id) {
                    latestOther.till = newRegistration.till;
                    extendedOther = true;
                } else {
                    newRegistration.from = latestOther.till;
                }
            }
        } else {
            // Check wheter we could extend a current excisting registration
            this.registrations().forEach((registration) => {
                if (!extendedOther && registration.work_project_id == project) {
                    // Check if there is any other registration between registration.till and newRegistration.from
                    const hasRegistrationBetween = (from: Date, till: Date) =>
                        this.registrations().some((r) =>
                            r !== registration &&
                            this.isBefore(new Date(r.from), till) &&
                            this.isBefore(from, new Date(r.till)),
                        );

                    if (this.isSame(new Date(registration.from), newRegistration.till)) {
                        registration.from = newRegistration.from;
                        extendedOther = true;
                    } else if (
                        this.isSame(new Date(registration.till), newRegistration.from) &&
                        !hasRegistrationBetween(new Date(registration.till), newRegistration.till)
                    ) {
                        registration.till = newRegistration.till;
                        extendedOther = true;
                    } else if (
                        event.type == 'mousemove' &&
                        this.isBefore(new Date(registration.till), newRegistration.from) &&
                        this.getDifferenceInMinutes(newRegistration.from, new Date(registration.till)) <= 30 &&
                        !hasRegistrationBetween(new Date(registration.till), newRegistration.till)
                    ) {
                        registration.till = newRegistration.till;
                        extendedOther = true;
                    }
                }
            });
        }

        // When we didn't extend another registration, push the new registration object
        if (!extendedOther) this.registrations().push(newRegistration);

        // Check wheter we could simplify the registration
        // This is the case when we register subsequent registrations for same projects
        let lastRegistration = null;
        this.registrations()
            .sort((a, b) => {
                return new Date(a.from).getTime() - new Date(b.from).getTime();
            })
            .forEach((registration) => {
                if (
                    lastRegistration &&
                    this.isSame(new Date(lastRegistration.till), new Date(registration.from)) &&
                    lastRegistration.work_project_id == registration.work_project_id
                ) {
                    lastRegistration.till = registration.till;
                    this.registrations().splice(this.registrations().indexOf(registration), 1);
                }

                lastRegistration = registration;
            });

        this.ref.detectChanges();

        this.isWorking = false;
    }

    public quarterIsWholeHour(quarter) {
        return new Date(quarter).getMinutes() === 0;
    }

    public officeTime(quarter) {
        let hour = new Date(quarter).getHours();
        return hour > 7 && hour < 17;
    }

    public hasComment(time) {
        let indexTime = new Date(time).toString().substring(16, 21);
        return this.comments().hasOwnProperty(indexTime);
    }

    public getCommentsArray(): { key: string; value: string }[] {
        return Object.entries(this.comments()).map(([key, value]) => ({key, value}));
    }

    public isToday(date: Date) {
        return this.isSameDay(date, new Date());
    }

    public earliestEditableDate(): Date {
        return Utils.earliestEditableDate(this.currentTime, AuthorisationService.hasRole(this.Roles.MutationPreviousPeriod), AuthorisationService.hasRole(this.Roles.MutationPrevPreviousPeriod));
    }

    public isFuture(date: Date) {
        return this.getStartOfDay(date) > this.getStartOfDay(new Date());
    }

    public isWeekend(date: Date) {
        const day = date.getDay();
        return day === 0 || day === 6; // Sunday = 0, Saturday = 6
    }

    public today() {
        this.selectedDateChanged(new Date());
    }

    public removeVisible(project: Project) {
        this.searchNewProjects.set([]);
        this.searchNewProjectText.setValue('');
        this.projectService.toggleVisible(project, false).then(() => {
            this.loadRegistrations();
        });
    }

    public addVisible(project: Project) {
        this.searchNewProjects.set([]);
        this.searchNewProjectText.setValue('');
        this.projectService.toggleVisible(project, true).then(() => {
            this.loadRegistrations();
        });
    }

    public quarterSelected(project, quarter) {
        let quarterTime = new Date(quarter).getTime();

        return (
            this.registrations()
                .filter((p) => p.work_project_id === project)
                .filter(
                    (registration) =>
                        new Date(registration.from).getTime() <= quarterTime &&
                        new Date(registration.till).getTime() > quarterTime,
                ).length > 0
        );
    }

    // Presentation helpers (labels, grid geometry, popover position); they only read state

    /** "Vandaag · di 13 okt 2026" */
    public dateLabel(): string {
        return (this.isToday(this.selectedDate) ? 'Vandaag · ' : '') + this.niceDate(this.selectedDate, true);
    }

    public totalLabel(): string {
        return this.isToday(this.selectedDate) ? 'Totaal vandaag' : 'Totaal ' + this.niceDate(this.selectedDate);
    }

    /** "di 13 okt" (+ " 2026") */
    public niceDate(date: Date, withYear = false): string {
        return formatDate(date, withYear ? 'EEE d MMM y' : 'EEE d MMM', 'nl');
    }

    public previousDay() {
        this.goToDate(this.addDays(this.selectedDate, -1));
    }

    public nextDay() {
        this.goToDate(this.addDays(this.selectedDate, 1));
    }

    public goToDate(date: Date | null) {
        if (date) {
            this.selectedDateChanged(new Date(date));
        }
    }

    /** The red line: position in % of the hours area, or null when the selected day is not today. */
    public nowLeft(): number | null {
        const now = this.now();
        if (!this.isSameDay(now, this.selectedDate)) {
            return null;
        }
        return (now.getHours() * 60 + now.getMinutes()) / (24 * 60) * 100;
    }

    /** "Opslaan…" while saving, "Opgeslagen" (muted) without changes, otherwise "Opslaan". */
    public saveLabel(): string {
        if (this.saveButtonText() === 'Opslaan…') {
            return 'Opslaan…';
        }
        return this.snapshot() === this.savedSnapshot ? 'Opgeslagen' : 'Opslaan';
    }

    public isNowHour(hour: HourCell): boolean {
        return this.isSameDay(this.now(), this.selectedDate) && this.now().getHours() === hour.hour;
    }

    /** Registrations of one row as rounded blocks (adjacent registrations merged), in % of the hours area. */
    public blocksFor(projectId: number): Block[] {
        const dayStart = this.getStartOfDay(this.selectedDate).getTime();
        const spans = this.registrations()
            .filter(registration => registration.work_project_id == projectId)
            .map(registration => ({from: new Date(registration.from).getTime(), till: new Date(registration.till).getTime()}))
            .filter(item => Number.isFinite(item.from) && Number.isFinite(item.till) && item.till > item.from)
            .sort((a, b) => a.from - b.from);
        const merged: { from: number; till: number }[] = [];
        for (const item of spans) {
            const last = merged[merged.length - 1];
            if (last && item.from <= last.till) {
                last.till = Math.max(last.till, item.till);
            } else {
                merged.push({...item});
            }
        }
        return merged
            .map(item => {
                const from = Math.max(item.from, dayStart);
                const till = Math.min(item.till, dayStart + DAY_MS);
                return {from: item.from, left: (from - dayStart) / DAY_MS * 100, width: (till - from) / DAY_MS * 100};
            })
            .filter(block => block.width > 0);
    }

    /** Orange dot: the quarter has a comment and this row has registered it. */
    public cellHasNote(quarter: QuarterCell, row: RegistrationRow): boolean {
        return this.comments().hasOwnProperty(quarter.key) && this.quarterSelected(row.project.id, quarter.time);
    }

    public cellIsActive(quarter: QuarterCell, row: RegistrationRow): boolean {
        return this.showCommentModal && this.commentQuarter?.time === quarter.time && this.commentRow?.project.id === row.project.id;
    }

    public cellTitle(quarter: QuarterCell): string {
        const comment = this.comments()[quarter.key];
        return quarter.label + (comment ? ' · ' + comment : '');
    }

    /** Right click on a quarter: the comment popover at the mouse. */
    public openCommentMenu(event: MouseEvent, quarter: QuarterCell, row: RegistrationRow) {
        this.placeCommentMenu(event.clientX + 8, event.clientY);
        this.commentRow = row;
        this.addComment({time: quarter.time}, row.project);
        this.focusComment();
        return false;
    }

    /** Click on a comment in the list: the popover above that comment. */
    public openCommentFromList(event: MouseEvent, key: string) {
        const quarter = this.quarters().find(q => this.commentKey(q.time) === key);
        if (!quarter) {
            return;
        }
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        this.placeCommentMenu(rect.left, rect.top - 8, true);
        this.commentRow = this.rowAt(quarter.time);
        this.addComment(quarter, this.commentRow?.project ?? null);
        this.focusComment();
    }

    public closeCommentMenu() {
        this.showCommentModal = false;
        return false;
    }

    public commentKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            this.setComment();
        }
    }

    public commentTitle(): string {
        return this.commentQuarter && this.hasComment(this.commentQuarter.time) ? 'Opmerking bewerken' : 'Opmerking toevoegen';
    }

    public commentSubtitle(): string {
        if (!this.commentQuarter) {
            return '';
        }
        const label = this.quarterLabel(new Date(this.commentQuarter.time));
        const row = this.commentRow;
        return row ? `${label} · ${row.client} › ${row.task}` : label;
    }

    /** The day's comments sorted by time, with the row that has the quarter registered. */
    public commentList(): { key: string; value: string; label: string; row: RegistrationRow | null }[] {
        const dayStart = this.getStartOfDay(this.selectedDate);
        return this.getCommentsArray()
            .sort((a, b) => a.key.localeCompare(b.key))
            .map(entry => {
                const [hours, minutes] = entry.key.split(':').map(Number);
                const date = new Date(dayStart);
                date.setHours(hours || 0, minutes || 0, 0, 0);
                return {...entry, label: this.quarterLabel(date), row: this.rowAt(date.toISOString())};
            });
    }

    // Search dropdown

    public searchOpen(): boolean {
        return this.searchFocused();
    }

    public searchState(): 'hint' | 'busy' | 'empty' | 'results' {
        const query = (this.searchQuery() ?? '').trim();
        if (this.searchResults().length) {
            return 'results';
        }
        if ((this.searchQuery() ?? '').length <= 2) {
            return 'hint';
        }
        return this.searchedFor() === this.searchQuery() && query ? 'empty' : 'busy';
    }

    public searchKeydown(event: KeyboardEvent) {
        const results = this.searchResults();
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.searchIndex.set(Math.min(this.searchIndex() + 1, Math.max(0, results.length - 1)));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.searchIndex.set(Math.max(this.searchIndex() - 1, 0));
        } else if (event.key === 'Enter') {
            const row = results[Math.min(this.searchIndex(), results.length - 1)];
            if (row) {
                event.preventDefault();
                this.addVisible(row.project);
            }
        } else if (event.key === 'Escape') {
            this.searchNewProjectText.setValue('');
            (event.target as HTMLElement).blur();
        }
    }

    public pickSearchResult(event: MouseEvent, row: RegistrationRow) {
        // mousedown: keep the focus in the search field
        event.preventDefault();
        this.addVisible(row.project);
    }

    /** Splits text into parts, marking where the search text occurs (case insensitive). */
    public highlight(text: string): { text: string; match: boolean }[] {
        const query = (this.searchQuery() ?? '').trim().toLowerCase();
        if (!text || !query) {
            return [{text: text ?? '', match: false}];
        }
        const parts: { text: string; match: boolean }[] = [];
        const lower = text.toLowerCase();
        let position = 0;
        let index = lower.indexOf(query);
        while (index >= 0) {
            if (index > position) {
                parts.push({text: text.substring(position, index), match: false});
            }
            parts.push({text: text.substring(index, index + query.length), match: true});
            position = index + query.length;
            index = lower.indexOf(query, position);
        }
        if (position < text.length) {
            parts.push({text: text.substring(position), match: false});
        }
        return parts;
    }

    private flatten(projects: Project[]): RegistrationRow[] {
        const rows: RegistrationRow[] = [];
        for (const project of projects ?? []) {
            const client = project.company_short || project.company_name || '';
            const clientFull = project.company_name || client;
            const make = (leaf: Project, path: string[]): RegistrationRow => ({
                project: leaf,
                task: leaf.name,
                sub: [client, ...path].filter(Boolean).join(' › '),
                client,
                clientFull,
                companyId: project.company_id ?? leaf.company_id ?? null,
                title: [clientFull, ...path, leaf.name].join(' » '),
            });
            if (!project.children?.length) {
                rows.push(make(project, []));
                continue;
            }
            for (const child of project.children) {
                if (!child.children?.length) {
                    rows.push(make(child, [project.name]));
                } else {
                    for (const subchild of child.children) {
                        rows.push(make(subchild, [project.name, child.name]));
                    }
                }
            }
        }
        return rows;
    }

    /** The row that has the given quarter registered (for the comment list and popover). */
    private rowAt(time: string): RegistrationRow | null {
        return this.rows().find(row => this.quarterSelected(row.project.id, time)) ?? null;
    }

    private placeCommentMenu(x: number, y: number, above = false) {
        const width = 320;
        const height = 250;
        const left = Math.max(8, Math.min(x, window.innerWidth - width - 16));
        const fitsBelow = y + 12 + height < window.innerHeight;
        const top = above || !fitsBelow ? y - height - 12 : y + 12;
        this.commentPosition = {x: left, y: Math.max(8, top)};
    }

    private focusComment() {
        afterNextRender(() => {
            const textarea = this.commentElement?.nativeElement as HTMLTextAreaElement;
            textarea?.focus();
            textarea?.setSelectionRange(textarea.value.length, textarea.value.length);
        }, {injector: this.injector});
    }

    private scrollToFocus() {
        const scroller = this.registrationhours?.nativeElement;
        const hoursHead = scroller?.querySelector<HTMLElement>('.hours-head');
        if (!scroller || !hoursHead) {
            return;
        }
        const hourWidth = hoursHead.offsetWidth / 24;
        const view = scroller.clientWidth - hoursHead.offsetLeft;
        let left: number;
        if (this.isSameDay(this.now(), this.selectedDate)) {
            const now = this.now();
            left = (now.getHours() + now.getMinutes() / 60) * hourWidth - view / 2;
        } else {
            left = OPEN_AT_HOUR * hourWidth;
        }
        // Only the grid scrolls; scrollIntoView would also move the page
        scroller.scrollLeft = Math.max(0, left);
    }

    /** Registrations and comments as a comparable string (order independent). */
    private snapshot(): string {
        const registrations = this.registrations()
            .map(registration => [registration.work_project_id, new Date(registration.from).getTime(), new Date(registration.till).getTime()])
            .sort((a, b) => a[1] - b[1] || a[0] - b[0]);
        const comments = Object.entries(this.comments()).sort(([a], [b]) => a.localeCompare(b));
        return JSON.stringify([registrations, comments]);
    }

    private commentKey(time: string): string {
        return new Date(time).toString().substring(16, 21);
    }

    private quarterLabel(date: Date): string {
        const end = this.addMinutes(date, 15);
        return `${this.pad(date.getHours())}:${this.pad(date.getMinutes())}–${this.pad(end.getHours())}:${this.pad(end.getMinutes())}`;
    }

    private pad(value: number): string {
        return String(value).padStart(2, '0');
    }

    private dayKey(date: Date): string {
        return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    }

    private addDays(date: Date, days: number): Date {
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    // Helper methods for date manipulation
    private getStartOfDay(date: Date): Date {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    private addMinutes(date: Date, minutes: number): Date {
        return new Date(date.getTime() + minutes * 60000);
    }

    private isSameDay(date1: Date, date2: Date): boolean {
        return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        );
    }

    private isSameOrBefore(date1: Date, date2: Date): boolean {
        return date1.getTime() <= date2.getTime();
    }

    private isSameOrAfter(date1: Date, date2: Date): boolean {
        return date1.getTime() >= date2.getTime();
    }

    private isSame(date1: Date, date2: Date): boolean {
        return date1.getTime() === date2.getTime();
    }

    private isBefore(date1: Date, date2: Date): boolean {
        return date1.getTime() < date2.getTime();
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.showCommentModal = false;
        }

        if (event.ctrlKey === true && event.key === "s") {
            event.preventDefault()
            if (this.showCommentModal) {
                this.setComment();
            } else {
                this.save();
            }
        }
    }
}
